
import { useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContext } from '../../hooks/useAuth';
import { User } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';

interface UserProfile {
  profile_photo_url?: string;
  full_name?: string;
  role?: string;
}

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile data
  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('🔍 Fetching profile for user:', userId);
      const { data, error } = await supabase
        .from('user_profiles')
        .select('profile_photo_url, role')
        .eq('user_id', userId)
        .single();

      if (error) {
        console.error('❌ Error fetching user profile:', error);
        setUserProfile(null);
        return;
      }

      console.log('✅ Profile data fetched:', data);
      setUserProfile(data);
    } catch (error) {
      console.error('❌ Error in fetchUserProfile:', error);
      setUserProfile(null);
    }
  };

  // Refresh profile manually
  const refreshProfile = async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  };

  // Upload profile photo
  const uploadProfilePhoto = async (file: File): Promise<boolean> => {
    if (!user?.id) {
      console.error('❌ No user logged in');
      alert('You must be logged in to upload a photo');
      return false;
    }

    try {
      console.log('📤 Starting profile photo upload...');
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('❌ Upload error:', uploadError);
        return false;
      }

      const { data: urlData } = await supabase.storage
        .from('profile-photos')
        .getPublicUrl(filePath);

      const photoUrl = urlData.publicUrl;

      const { error: updateError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          profile_photo_url: photoUrl,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });

      if (updateError) {
        console.error('❌ Profile update error:', updateError);
        return false;
      }

      setUserProfile(prev => ({
        ...prev,
        profile_photo_url: photoUrl
      }));
      
      return true;
    } catch (error) {
      console.error('❌ Error uploading profile photo:', error);
      return false;
    }
  };

  // Change password
  const changePassword = async (newPassword: string): Promise<boolean> => {
    try {
      console.log('🔐 Changing password...');
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        console.error('❌ Password change error:', error);
        return false;
      }

      console.log('✅ Password changed successfully');
      return true;
    } catch (error) {
      console.error('❌ Error changing password:', error);
      return false;
    }
  };

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      console.log('Auth state change:', _event);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    console.log('🚪 Signing out...');
    try {
      // Clear local state first
      setUser(null);
      setUserProfile(null);
      
      // Attempt to sign out from Supabase
      const { error } = await supabase.auth.signOut();
      
      // Ignore session missing errors - user is already logged out
      if (error && !error.message.includes('Auth session missing')) {
        console.error('❌ Sign out error:', error);
      }
      
      console.log('✅ Sign out successful');
    } catch (error: any) {
      console.error('❌ Error during sign out:', error);
      // Local state is already cleared, so logout is effective
    }
  };

  const logout = signOut;

  return (
    <AuthContext.Provider value={{ 
      user, 
      userProfile,
      loading, 
      signOut,
      refreshProfile,
      uploadProfilePhoto,
      changePassword,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};
